import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@/components/ui/icon';
import { useActiveRideStore } from '@/lib/core/stores/useActiveRideStore';
import { useLocaleStore } from '@/lib/core/stores/useLocaleStore';
import { useClientTranslation } from '@/lib/core/i18n';
import { useApp } from '@/lib/core/providers/appProvider';
import { getRidePhase, isRideInProgress } from '@/services/platform/data/booking/rides/rideStatus';
import { cn } from '@/lib/core/utils/cn';

interface RideProgressBannerProps {
  /** The app user's own first name (not the driver's) — same value the hero's default greeting shows. */
  firstName?: string | null;
}

/**
 * Home page ride-status banner — shown in place of the hero's personalized
 * greeting while a Range Rover ride is in progress, linking back into the
 * driver-info step. Renders both the inline (in-hero) and fixed-on-scroll
 * layouts; an IntersectionObserver watching the inline card itself toggles
 * the fixed one on only once the inline card has fully scrolled out of view.
 */
export function RideProgressBanner({ firstName }: RideProgressBannerProps) {
  const ride = useActiveRideStore(state => state.ride);
  const locale = useLocaleStore(state => state.locale);
  const { appSettings } = useApp();
  const t = useClientTranslation();
  const [now, setNow] = useState(() => Date.now());
  const [isFixed, setIsFixed] = useState(false);
  const sentinelRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const inlineCard = sentinelRef.current;
    if (!inlineCard) return;

    // The fixed bar should only appear once the inline card has fully scrolled out of
    // view — not as soon as it starts leaving — so it never overlaps/duplicates it.
    const observer = new IntersectionObserver(([entry]) => setIsFixed(!entry.isIntersecting), {
      threshold: 0,
    });
    observer.observe(inlineCard);
    return () => observer.disconnect();
  }, [ride]);

  if (!ride || !ride.driver || !isRideInProgress(ride.status)) return null;

  const phase = getRidePhase(ride.status);
  const minutesToArrival = ride.eta
    ? Math.max(0, Math.round((new Date(ride.eta).getTime() - now) / 60000))
    : null;

  const statusLine =
    phase === 'arrived'
      ? t('App.Labels.DriverHasArrived')
      : phase === 'transferring'
        ? t('App.Labels.EnRouteToDestination')
        : minutesToArrival != null
          ? t('App.Labels.RideProgressArrivingIn', { count: minutesToArrival })
          : t('App.Labels.DriverArrivesIn');

  const destination = ride.dropoffName ?? ride.dropoffAddress ?? '';
  const destinationLine = destination ? t('App.Labels.RideProgressDestination', { destination }) : '';
  const plate = ride.car?.plateNumber;
  const href =
    appSettings?.properties?.bookingRangeRover?.route?.path ??
    `/${locale.split('-')[0]}/book/range-rover-flow/`;

  return (
    <>
      <Link
        to={href}
        ref={sentinelRef}
        className="border-jncquoi-neutral-900 bg-jncquoi-neutral-1000/80 mx-4 block rounded-[10px] border-[0.5px] px-4 py-4 text-left backdrop-blur-[5px]"
      >
        {firstName && (
          <span
            className="font-didot block text-[36px] text-white italic"
            style={{ letterSpacing: '-1.44px', lineHeight: '34px' }}
          >
            {firstName}
          </span>
        )}
        <div className="mt-2 flex items-center justify-between gap-4">
          <span className="font-pp-neue-montreal text-[16px] font-semibold text-white">{statusLine}</span>
          <Icon name="car" width={20} height={20} className="shrink-0 text-white" />
        </div>
        {destinationLine && (
          <div className="mt-1 flex items-center justify-between gap-4">
            <span className="font-pp-neue-montreal text-[14px] text-white">{destinationLine}</span>
            {plate && <span className="font-pp-neue-montreal text-[14px] text-white">{plate}</span>}
          </div>
        )}
      </Link>

      <Link
        to={href}
        aria-hidden={!isFixed}
        tabIndex={isFixed ? undefined : -1}
        className={cn(
          'border-jncquoi-neutral-900 bg-jncquoi-neutral-1000/80 fixed inset-x-0 z-50 mx-4 block rounded-[10px] border-[0.5px] p-4 backdrop-blur-[5px] transition-transform duration-300',
          isFixed ? 'top-[calc(env(safe-area-inset-top)+44px+16px+16px)]' : 'top-0 -translate-y-[calc(200%)]'
        )}
      >
        <div className="flex items-center justify-between gap-4">
          <span className="font-pp-neue-montreal text-[14px] font-semibold text-white">{statusLine}</span>
          <Icon name="car" width={18} height={18} className="shrink-0 text-white" />
        </div>
        {destinationLine && (
          <div className="mt-0.5 flex items-center justify-between gap-4">
            <span className="font-pp-neue-montreal text-[12px] text-white">{destinationLine}</span>
            {plate && <span className="font-pp-neue-montreal text-[12px] text-white">{plate}</span>}
          </div>
        )}
      </Link>
    </>
  );
}
